/**
 * Preserves the deep link a logged-out user was trying to reach across the
 * Cognito Hosted UI round trip, so `IndexRedirect` can send them there
 * instead of the hardcoded default landing page after sign-in.
 *
 * Cognito's `redirect_uri` is fixed to `${origin}/` (see `user-manager.ts`),
 * so the original path can't survive via the URL itself — sessionStorage
 * carries it across the full-page navigation to Cognito and back.
 */
const STORAGE_KEY = 'matic:post_login_redirect';

/** Paths that should never be stashed as a return destination. */
const EXCLUDED_PATHS = new Set(['/', '/login', '/logout']);

export function stashReturnTo(pathAndSearch: string): void {
  if (EXCLUDED_PATHS.has(pathAndSearch)) {
    return;
  }

  try {
    globalThis.sessionStorage.setItem(STORAGE_KEY, pathAndSearch);
  } catch {
    // SessionStorage unavailable (private browsing, etc.) — fall back to
    // the default landing page rather than failing the login flow.
  }
}

/** Reads and clears the stashed return path, if any. */
export function consumeReturnTo(): string | undefined {
  try {
    const value = globalThis.sessionStorage.getItem(STORAGE_KEY);
    if (!value) {
      return undefined;
    }

    globalThis.sessionStorage.removeItem(STORAGE_KEY);
    return value;
  } catch {
    return undefined;
  }
}
